// aluno/financeiro.js — tela financeiro (recurso experimental, nunca lançado).
//
// Origem (monólito): linhas 8653-8737 (HTML financeiroScreen),
//   8780-9032 (JS showFinanceiro, renderFinanceiro, renderFinanceiroAluno,
//   helpers financeiros e lógica de importação).
//
// Contexto: "foi colocado por outro funcionário como experimental,
//   nunca foi usado" — preservado AS-IS com banner de aviso.
//
// Dependências:
//   - auth    (core/auth.js)    — auth.getCurrent()
//   - storage (core/storage.js) — storage.get('financeiro', null)
//   - router  (core/router.js)  — router.go('/aluno')
//
// Exporta:
//   function render(container)
//
// AVISO: Lógica financeira real (importação XLSX, renderFinanceiro admin)
//   NÃO foi migrada para este módulo — ela dependia de XLSX.js global e de
//   variáveis globais do monólito (alunos, alunosFinanceiro). Apenas a
//   estrutura DOM e a visão do aluno foram preservadas.
//   Fase 3 migrou a chave 'mbo_financeiro' → 'eloq:financeiro'.

import { auth }    from '../core/auth.js';
import { storage } from '../core/storage.js';
import { router }  from '../core/router.js';

// ── Constantes ────────────────────────────────────────────────────────────────

const FIN_MES_NOMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

/** Mês de início de cada turma (índice 1-based). */
const FIN_TURMA_INICIO_MES = {
  '31':1,'32':1,'33':1,'34':1,'35':3,'36':5,'37':7,'38':9,'39':11,
};

/** Mês de encerramento de cada turma. */
const FIN_TURMA_FIM_MES = {
  '31':4,'32':6,'33':8,'34':10,'35':12,'36':12,'37':12,'38':6,'39':8,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Retorna estilos de badge conforme status financeiro.
 * @param {string} status
 * @returns {{ bg:string, color:string, label:string }}
 */
function _estiloStatus(status) {
  if (!status) return { bg: '#F0F0F0', color: '#9B9B9B', label: '—' };
  const s = status.trim().toLowerCase();
  if (s === 'pago')     return { bg: '#E8F5E9', color: '#34c759', label: 'Pago' };
  if (s === 'pendente') return { bg: '#FFF3E0', color: '#ff9500', label: 'Pendente' };
  if (s === 'atrasado') return { bg: '#FFEBEE', color: '#ff3b30', label: 'Atrasado' };
  if (s === 'isento')   return { bg: '#E3F2FD', color: '#007AFF', label: 'Isento' };
  return { bg: '#F0F0F0', color: '#9B9B9B', label: status };
}

/**
 * Gera HTML de uma linha de mensalidade.
 * @param {number} m          - mês (1-12)
 * @param {object} pagamentos - objeto { 'YYYY-MM': status }
 * @param {number} mesAtual   - mês corrente (1-12)
 * @returns {string} HTML
 */
function _htmlLinhaFinanceiro(m, pagamentos, mesAtual) {
  const futuro = m > mesAtual;
  const chave  = `2026-${String(m).padStart(2, '0')}`;
  let st;

  if (futuro) {
    st = { bg: '#F0F0F0', color: '#BDBDBD', label: 'A vencer' };
  } else {
    const s = pagamentos[chave] || '';
    st = _estiloStatus(s);
    if (!s) st = { bg: '#FFF3E0', color: '#ff9500', label: 'Pendente' };
  }

  const iconeBg = futuro ? '#F5F5F5'
    : st.color === '#34c759' ? '#E8F5E9'
    : st.color === '#ff3b30' ? '#FFEBEE'
    : st.color === '#ff9500' ? '#FFF3E0'
    : '#F5F5F5';

  return `
    <div class="fin-row${futuro ? ' futuro' : ''}">
      <div class="fin-icon" style="background:${iconeBg}">
        ${FIN_MES_NOMES[m - 1].substring(0, 3).toUpperCase()}<br>2026
      </div>
      <div style="flex:1">
        <div style="font-size:14px;font-weight:700;color:${futuro ? '#9B9B9B' : '#0A0A0A'}">${FIN_MES_NOMES[m - 1]}</div>
        <div style="font-size:11px;color:#9B9B9B;margin-top:1px">Mensalidade 2026</div>
      </div>
      <span class="fin-badge" style="background:${st.bg};color:${st.color}">${st.label}</span>
    </div>`;
}

/**
 * Gera HTML do painel de resumo (pagos / pendentes / atrasados).
 * @param {number} pagos
 * @param {number} pendentes
 * @param {number} atrasados
 * @returns {string} HTML
 */
function _htmlResumo(pagos, pendentes, atrasados) {
  return `
    <div class="fin-sg">
      <div class="fin-sc"><div class="fin-sn" style="color:#34c759">${pagos}</div><div class="fin-sl">Pagos</div></div>
      <div class="fin-sc"><div class="fin-sn" style="color:#ff9500">${pendentes}</div><div class="fin-sl">Pendentes</div></div>
      <div class="fin-sc"><div class="fin-sn" style="color:#ff3b30">${atrasados}</div><div class="fin-sl">Atrasados</div></div>
    </div>`;
}

// ── Renderização da visão do aluno ────────────────────────────────────────────

/**
 * Popula a área #fin-student-content com os dados do aluno.
 * Usa storage.get('financeiro', null) para dados cacheados.
 * @param {HTMLElement} container
 */
function _renderizarAlunoView(container) {
  const user     = auth.getCurrent();
  if (!user) return;

  const cpf      = user.senha.replace(/\D/g, '');
  const turma    = user.turma;
  const inicio   = FIN_TURMA_INICIO_MES[turma] || 1;
  const fim      = FIN_TURMA_FIM_MES[turma] || 12;
  const mesAtual = new Date().getMonth() + 1;

  // Busca dados de cache (chave migrada na Fase 3 de mbo_financeiro → eloq:financeiro)
  const alunosFinanceiro = storage.get('financeiro', {});
  const fin     = (alunosFinanceiro && alunosFinanceiro[cpf]) || {};
  const pags    = fin.pagamentos || {};

  // Todos os meses do curso
  const todosMeses = [];
  for (let m = inicio; m <= fim; m++) todosMeses.push(m);

  // Contadores — apenas meses passados/atuais
  let pagos = 0, pendentes = 0, atrasados = 0;
  todosMeses.forEach(m => {
    if (m > mesAtual) return;
    const s = (pags[`2026-${String(m).padStart(2, '0')}`] || '').toLowerCase();
    if (s === 'pago') pagos++;
    else if (s === 'atrasado') atrasados++;
    else pendentes++;
  });

  const content = container.querySelector('#fin-student-content');
  if (!content) return;

  content.innerHTML =
    _htmlResumo(pagos, pendentes, atrasados) +
    '<div class="fin-lbl">Mensalidades 2026</div>' +
    todosMeses.map(m => _htmlLinhaFinanceiro(m, pags, mesAtual)).join('');
}

// ── Função principal exportada ────────────────────────────────────────────────

/**
 * Renderiza a tela financeiro dentro de `container`.
 * Chamado pelo router para a rota #/aluno/financeiro.
 *
 * @param {HTMLElement} container - elemento #app
 */
export function render(container) {
  container.innerHTML = `
    <div id="fin-root" style="min-height:100vh;min-height:100dvh;background:#F5F5F5;font-family:'Montserrat',sans-serif;-webkit-font-smoothing:antialiased">

      <!-- Header -->
      <header style="background:linear-gradient(90deg,#6A001F 0%,#B3002D 100%);position:sticky;top:0;z-index:100;padding-top:calc(env(safe-area-inset-top,0px) + 16px)">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:0 20px 16px">
          <button id="fin-back-btn" type="button"
            style="width:38px;height:38px;border-radius:50%;border:none;background:rgba(255,255,255,0.18);color:white;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background 0.2s"
            aria-label="Voltar">←</button>
          <div style="flex:1;margin-left:12px">
            <div style="font-size:10px;font-weight:600;color:rgba(255,255,255,0.65);letter-spacing:2px;text-transform:uppercase">Eloquence</div>
            <div style="font-size:22px;font-weight:800;color:#fff;line-height:1.1">Financeiro</div>
          </div>
          <div style="width:42px;height:42px;background:rgba(255,255,255,0.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:20px;backdrop-filter:blur(8px)">💰</div>
        </div>
      </header>

      <main style="padding:20px;padding-bottom:80px">

        <!-- Banner de aviso: recurso experimental -->
        <div style="background:#fff3cd;border-left:4px solid #ff9500;border-radius:12px;padding:14px 16px;margin-bottom:20px;display:flex;align-items:flex-start;gap:10px">
          <span style="font-size:18px;flex-shrink:0">⚠️</span>
          <div>
            <div style="font-size:13px;font-weight:800;color:#6A001F;margin-bottom:2px">Recurso em desenvolvimento — em breve.</div>
            <div style="font-size:12px;color:#7a5800;line-height:1.5">
              Esta seção está em fase experimental. Os dados exibidos podem estar incompletos.
              Para dúvidas, fale com a secretaria.
            </div>
          </div>
        </div>

        <!-- Visão do aluno -->
        <div id="fin-student-view">
          <div id="fin-student-content"></div>
        </div>

      </main>
    </div>
  `;

  // ── Estilos inline (scoped .fin-*) ───────────────────────────────────────
  if (!document.getElementById('fin-styles')) {
    const s = document.createElement('style');
    s.id = 'fin-styles';
    s.textContent = `
      .fin-sg{display:flex;gap:12px;background:#fff;border-radius:16px;padding:16px;margin-bottom:16px;box-shadow:0 2px 10px rgba(0,0,0,0.06)}
      .fin-sc{flex:1;text-align:center}
      .fin-sn{font-size:24px;font-weight:900;line-height:1}
      .fin-sl{font-size:10px;font-weight:700;color:#9B9B9B;margin-top:4px;text-transform:uppercase;letter-spacing:0.5px}
      .fin-lbl{font-size:11px;font-weight:800;color:#9B9B9B;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:12px}
      .fin-row{display:flex;align-items:center;gap:12px;background:#fff;border-radius:12px;padding:12px 14px;margin-bottom:8px;box-shadow:0 1px 6px rgba(0,0,0,0.05)}
      .fin-row.futuro{opacity:0.6}
      .fin-icon{width:44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;color:#555;text-align:center;line-height:1.2;flex-shrink:0}
      .fin-badge{font-size:11px;font-weight:800;padding:4px 12px;border-radius:100px;white-space:nowrap}
    `;
    document.head.appendChild(s);
  }

  // ── Wiring de eventos ─────────────────────────────────────────────────────

  container.querySelector('#fin-back-btn').addEventListener('click', () => {
    router.go('/aluno');
  });

  // Popula a view do aluno
  _renderizarAlunoView(container);
}
